import { ActionIcon, Card, Group, Stack } from '@mantine/core';
import { useNavigate } from 'react-router';
import { Page } from '../../../../api/pages-client/pages-client.type';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { ComponentPreview } from './component-preview';

type Props = {
  page: Page;
  onEdit: (page: Page) => void;
  onDelete: (page: Page) => void;
};

export const DashboardListItem = ({ page, onEdit, onDelete }: Props) => {
  const i18n = useTranslation();
  const navigate = useNavigate();

  const handleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onEdit(page);
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDelete(page);
  };

  return (
    <Card shadow="sm" radius="md" withBorder h={180} style={{ display: 'flex', flexDirection: 'column' }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb={10}>
        <Stack style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="sm">
              <Text
                weight="bold"
                size="large"
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '325px',
                  cursor: 'pointer',
                }}
                onClick={() => navigate(`/application/dashboards/${page.id}`)}
              >
                {page.name}
              </Text>
              <Tooltip
                label={
                  <Stack gap={0}>
                    <Text size="small" weight="medium">
                      {page.name}
                    </Text>
                    {page.description && <Text size="tiny">{page.description}</Text>}
                  </Stack>
                }
                variant="black"
                multiline
                style={{ maxWidth: 300 }}
                zIndex={1000}
              >
                <Icon name="info" size={20} />
              </Tooltip>
            </Group>
            <Group gap="sm">
              <Tooltip label={`Edit ${i18n.t('common.dashboard.dashboard')}`} variant="black">
                <ActionIcon variant="subtle" onClick={handleEdit}>
                  <Icon name="edit" size={20} style={{ cursor: 'pointer' }} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={`Delete ${i18n.t('common.dashboard.dashboard')}`} variant="black">
                <ActionIcon variant="subtle" onClick={handleDelete}>
                  <Icon name="delete" size={20} style={{ cursor: 'pointer' }} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        </Stack>
      </Group>
      {(page.components?.length && page.components?.length > 0) ||
      (page.data_entry_components?.length && page.data_entry_components?.length > 0) ? (
        <div
          style={{
            width: '100%',
            height: '100%',
            overflow: 'auto',
            position: 'relative',
            padding: '4px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            display: 'grid',
            // grammar-check disable-next-line ENGLISH_WORD_REPEAT_RULE
            gridTemplateColumns: '1fr 1fr',
            gap: '8px',
            alignContent: 'start',
          }}
        >
          {page.components?.map((component, index) => {
            return <ComponentPreview key={`component-${index}`} component={component} />;
          })}
          {page.data_entry_components?.map((dataEntryComponent, index) => {
            return <ComponentPreview key={`data-entry-${index}`} dataEntryComponent={dataEntryComponent} />;
          })}
        </div>
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Text size="small" weight="medium">
            {`No ${i18n.t('common.dashboard.widgets')} Added`}
          </Text>
        </div>
      )}
    </Card>
  );
};
